import React, { useEffect, useState } from 'react';

import classNames from 'classnames';
import loadable from '@loadable/component';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { richText } from '../../util/richText';
import { formatMoney } from '../../util/currency';
import { parse, stringify, createSlug } from '../../util/urlHelpers';
import { userDisplayNameAsString } from '../../util/data';
import { createResourceLocatorString } from '../../util/routes';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { useLocation, useHistory } from 'react-router-dom';
import {
  displayDeliveryPickup,
  displayDeliveryShipping,
  displayPrice,
} from '../../util/configHelpers';
import {
  propTypes,
  AVAILABILITY_MULTIPLE_SEATS,
  LISTING_STATE_CLOSED,
  LINE_ITEM_NIGHT,
  LINE_ITEM_DAY,
  LINE_ITEM_HOUR,
  LINE_ITEM_FIXED,
  LINE_ITEM_ITEM,
  STOCK_MULTIPLE_ITEMS,
  STOCK_INFINITE_MULTIPLE_ITEMS,
  LISTING_STATE_PUBLISHED,
} from '../../util/types';

import {
  OFFER,
  REQUEST,
  getSupportedProcessesInfo,
  isBookingProcess,
  isNegotiationProcess,
  isInquiryProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { fetchAuthor } from '../../containers/ListingPage/ListingPage.duck';

import { H4, AvatarMedium, ModalInMobile, PrimaryButton, InlineTextButton } from '../../components';

import PriceVariantPicker from './PriceVariantPicker/PriceVariantPicker';
import SubmitFinePrint from './SubmitFinePrint/SubmitFinePrint';

import css from './OrderPanel.module.css';

import { FaStar } from 'react-icons/fa';
import { ReactComponent as EmailSVG } from '../../assets/svg/email.svg';
import { ReactComponent as GlobeSVG } from '../../assets/svg/globe.svg';
import { ReactComponent as PhoneSVG } from '../../assets/svg/phone.svg';
import { ReactComponent as ShareSVG } from '../../assets/svg/share.svg';
import { ReactComponent as VerifiedSVG } from '../../assets/svg/verified.svg';
import { ReactComponent as WhatsAppSVG } from '../../assets/svg/whats-app.svg';
import { ReactComponent as XSVG } from '../../assets/svg/x.svg';
import { ReactComponent as LinkedInSVG } from '../../assets/svg/linkedIn.svg';
import { ReactComponent as InstagramSVG } from '../../assets/svg/instagram.svg';
import { ReactComponent as PDFIconSVG } from '../../assets/svg/pdfIcon.svg';

const BookingTimeForm = loadable(() =>
  import(/* webpackChunkName: "BookingTimeForm" */ './BookingTimeForm/BookingTimeForm')
);
const BookingDatesForm = loadable(() =>
  import(/* webpackChunkName: "BookingDatesForm" */ './BookingDatesForm/BookingDatesForm')
);
const BookingFixedDurationForm = loadable(() =>
  import(
    /* webpackChunkName: "BookingFixedDurationForm" */ './BookingFixedDurationForm/BookingFixedDurationForm'
  )
);
const InquiryWithoutPaymentForm = loadable(() =>
  import(
    /* webpackChunkName: "InquiryWithoutPaymentForm" */ './InquiryWithoutPaymentForm/InquiryWithoutPaymentForm'
  )
);
const ProductOrderForm = loadable(() =>
  import(/* webpackChunkName: "ProductOrderForm" */ './ProductOrderForm/ProductOrderForm')
);

// This defines when ModalInMobile shows content as Modal
const TODAY = new Date();
const DESC_MAX_WORDS = 300;
const MODAL_BREAKPOINT = 279;
const MIN_LENGTH_FOR_LONG_WORDS = 10;
const NegotiationForm = loadable(() =>
  import(/* webpackChunkName: "NegotiationForm" */ './NegotiationForm/NegotiationForm')
);

const NegotiationRequestQuoteForm = loadable(() =>
  import(
    /* webpackChunkName: "NegotiationRequestQuoteForm" */ './NegotiationRequestQuoteForm/NegotiationRequestQuoteForm'
  )
);

const isPublishedListing = listing => {
  return listing.attributes.state === LISTING_STATE_PUBLISHED;
};

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const getCheapestPriceVariant = (priceVariants = []) => {
  return priceVariants.reduce((cheapest, current) => {
    return current.priceInSubunits < cheapest.priceInSubunits ? current : cheapest;
  }, priceVariants[0]);
};

const formatMoneyIfSupportedCurrency = (price, intl) => {
  try {
    return formatMoney(intl, price);
  } catch (e) {
    return `(${price.currency})`;
  }
};

const openOrderModal = (isOwnListing, isClosed, history, location) => {
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ ...parse(search), orderOpen: true })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeOrderModal = (history, location) => {
  const { pathname, search, state } = location;
  const { orderOpen, ...searchParams } = parse(search);
  const searchString = `?${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

const handleSubmit = (isOwnListing, isClosed, isDirectSubmit, onSubmit, history, location) => {
  // TODO: currently, inquiry-process does not have any form to ask more order data.
  // We can submit without opening any inquiry/order modal.
  return isDirectSubmit
    ? () => onSubmit({})
    : () => openOrderModal(isOwnListing, isClosed, history, location);
};

const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };

const PriceMaybe = props => {
  const {
    price,
    publicData,
    validListingTypes,
    intl,
    marketplaceCurrency,
    showCurrencyMismatch = false,
  } = props;
  const { listingType, unitType } = publicData || {};

  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  const isPriceVariationsInUse = !!publicData?.priceVariationsEnabled;
  const hasMultiplePriceVariants = publicData?.priceVariants?.length > 1;

  if (!showPrice || !price || (isPriceVariationsInUse && hasMultiplePriceVariants)) {
    return null;
  }

  // Get formatted price or currency code if the currency does not match with marketplace currency
  const { formattedPrice, priceTitle } = priceData(price, marketplaceCurrency, intl);
  const priceValue = (
    <span className={css.priceValue}>{formatMoneyIfSupportedCurrency(price, intl)}</span>
  );
  const pricePerUnit = (
    <span className={css.perUnit}>
      <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
    </span>
  );

  // TODO: In CTA, we don't have space to show proper error message for a mismatch of marketplace currency
  //       Instead, we show the currency code in place of the price
  return showCurrencyMismatch ? (
    <div className={css.priceContainerInCTA}>
      <div className={css.priceValueInCTA} title={priceTitle}>
        <FormattedMessage
          id="OrderPanel.priceInMobileCTA"
          values={{ priceValue: formattedPrice }}
        />
      </div>
      <div className={css.perUnitInCTA}>
        <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
      </div>
    </div>
  ) : (
    <div className={css.priceContainer}>
      <p className={css.price}>
        <FormattedMessage id="OrderPanel.price" values={{ priceValue, pricePerUnit }} />
      </p>
    </div>
  );
};

const PriceMissing = () => {
  return (
    <p className={css.error}>
      <FormattedMessage id="OrderPanel.listingPriceMissing" />
    </p>
  );
};
const InvalidCurrency = () => {
  return (
    <p className={css.error}>
      <FormattedMessage id="OrderPanel.listingCurrencyInvalid" />
    </p>
  );
};

const InvalidPriceVariants = () => {
  return (
    <p className={css.error}>
      <FormattedMessage id="OrderPanel.listingPriceVariantsAreInvalid" />
    </p>
  );
};

const hasUniqueVariants = priceVariants => {
  const priceVariantsSlugs = priceVariants?.map(variant =>
    variant.name ? createSlug(variant.name) : 'no-name'
  );
  return new Set(priceVariantsSlugs).size === priceVariants.length;
};

const hasValidPriceVariants = priceVariants => {
  const isArray = Array.isArray(priceVariants);
  const hasItems = isArray && priceVariants.length > 0;
  const variantsHaveNames = hasItems && priceVariants.every(variant => variant.name);
  const namesAreUnique = hasItems && hasUniqueVariants(priceVariants);

  return variantsHaveNames && namesAreUnique;
};

/**
 * @typedef {Object} ListingTypeConfig
 * @property {string} listingType - The type of the listing
 * @property {string} transactionType - The type of the transaction
 * @property {string} transactionType.process - The process descriptionof the transaction
 * @property {string} transactionType.alias - The alias of the transaction process
 * @property {string} transactionType.unitType - The unit type of the transaction
 */

/**
 * OrderPanel is a component that renders a panel for making bookings, purchases, or inquiries for a listing.
 * It handles different transaction processes and displays appropriate forms based on the listing type.
 *
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overwrites the default class for the root element
 * @param {string} [props.className] - Custom class that extends
 * @param {string} [props.titleClassName] - Custom class name for the title
 * @param {propTypes.listing} props.listing - The listing data (either regular or own listing)
 * @param {Array<ListingTypeConfig>} props.validListingTypes - Array of valid listing type configurations
 * @param {boolean} [props.isOwnListing=false] - Whether the listing belongs to the current user
 * @param {listingType.user|listingType.currentUser} props.author - The listing author's user data
 * @param {ReactNode} [props.authorLink] - Custom component for rendering the author link
 * @param {ReactNode} [props.payoutDetailsWarning] - Warning message about payout details
 * @param {Function} props.onSubmit - Handler for form submission
 * @param {ReactNode|string} props.title - Title of the panel
 * @param {ReactNode} [props.titleDesktop] - Alternative title for desktop view
 * @param {ReactNode|string} [props.subTitle] - Subtitle text
 * @param {Function} props.onManageDisableScrolling - Handler for managing scroll behavior
 * @param {Function} props.onFetchTimeSlots - Handler for fetching available time slots
 * @param {Object} [props.monthlyTimeSlots] - Available time slots by month
 * @param {Function} props.onFetchTransactionLineItems - Handler for fetching transaction line items
 * @param {Function} [props.onContactUser] - Handler for contacting the listing author
 * @param {Array} [props.lineItems] - Array of line items for the transaction
 * @param {boolean} props.fetchLineItemsInProgress - Whether line items are being fetched
 * @param {Object} [props.fetchLineItemsError] - Error object if line items fetch failed
 * @param {string} props.marketplaceCurrency - The currency used in the marketplace
 * @param {number} props.dayCountAvailableForBooking - Number of days available for booking
 * @param {string} props.marketplaceName - Name of the marketplace
 *
 * @returns {JSX.Element} Component that displays the order panel with appropriate form
 */
const OrderPanel = props => {
  const [mounted, setMounted] = useState(false);
  const [fullDesc, setFullDesc] = useState(false);
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    setMounted(true);
  }, []);
  const {
    rootClassName,
    className,
    titleClassName,
    listing,
    reviewsData,
    validListingTypes,
    lineItemUnitType: lineItemUnitTypeMaybe,
    isOwnListing,
    onSubmit,
    title,
    titleDesktop,
    author,
    authorLink,
    onManageDisableScrolling,
    onFetchTimeSlots,
    monthlyTimeSlots,
    timeSlotsForDate,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    marketplaceCurrency,
    dayCountAvailableForBooking,
    marketplaceName,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
    callFetchAuthor,
    isCurrentListingRfq,
    currentAuthor,
    productTitle,
    productDesc,
    productBrandName,
  } = props;
  const { avgRating = 0 } = reviewsData;
  const publicData = listing?.attributes?.publicData || {};
  const description = listing?.attributes?.description || '';

  const authorEmail = currentAuthor?.attributes?.email;
  const services = currentAuthor?.attributes?.profile?.publicData?.services || [];
  const companyName = currentAuthor?.attributes?.profile?.publicData?.companyName;
  const isPremiumVendor = currentAuthor?.attributes?.profile?.publicData?.isPremiumVendor;

  const callNumber = currentAuthor?.attributes?.profile?.publicData?.phoneNumber?.number;
  const callDialCode = currentAuthor?.attributes?.profile?.publicData?.phoneNumber?.dialCode;
  const vendorCallNumber = `${callDialCode}${callNumber}`;
  const whatsAppNumber =
    currentAuthor?.attributes?.profile?.publicData?.alternativePhoneNumber?.number;
  const whatsAppDialCode =
    currentAuthor?.attributes?.profile?.publicData?.alternativePhoneNumber?.dialCode;
  const vendorWhatsAppNumber = `${whatsAppDialCode}${whatsAppNumber}`;

  const websiteLink = currentAuthor?.attributes?.profile?.publicData?.websiteLink;
  const socialMediaLink = currentAuthor?.attributes?.profile?.publicData?.socialMediaLink;
  const linkedInLink = currentAuthor?.attributes?.profile?.publicData?.linkedInLink;
  const xHandle = currentAuthor?.attributes?.profile?.publicData?.xHandle;

  const {
    unitType,
    brandName,
    brandPhoto,
    listingType,
    servicesType,
    category,
    subCategory,
    subSubCategory,
    additionalDocuments,
    transactionProcessAlias = '',
    priceVariants,
    startTimeInterval,
  } = publicData || {};

  const additionalUploadLink =
    currentAuthor?.attributes?.profile?.publicData?.additionalImageOrVedios ?? [];

  const categoryList = config?.categoryConfiguration?.categories;
  const result = {
    categories: categoryList.map(category => ({ id: category.id, name: category.name })),
    subCategories: [],
    subsubcategories: [],
  };

  categoryList.forEach(category => {
    category.subcategories.forEach(sub => {
      result.subCategories.push({ id: sub.id, name: sub.name });

      sub.subcategories.forEach(subSub => {
        result.subsubcategories.push({ id: subSub.id, name: subSub.name });
      });
    });
  });

  const categoryName = result?.categories?.filter(f => f?.id === category);
  const subCategoryName = result?.subCategories?.filter(f => f?.id === subCategory);
  const subSubCategoryName = result?.subsubcategories?.filter(f => f?.id === subSubCategory);
  const showCustomerNumber = publicData?.showNumber?.[0];
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const lineItemUnitType = lineItemUnitTypeMaybe || `line-item/${unitType}`;
  const trimmedDescription = fullDesc
    ? productDesc
    : productDesc?.length > DESC_MAX_WORDS
    ? productDesc.slice(0, DESC_MAX_WORDS) + '...'
    : productDesc;

  const price = listing?.attributes?.price;
  // const productTitle = listing?.attributes?.title || 'Unknown';
  const isInquiry = isInquiryProcess(processName);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const isNegotiation = isNegotiationProcess(processName);
  const isPaymentProcess = isBooking || isPurchase || isNegotiation;
  const showPriceMissing = isPaymentProcess && !isNegotiation && !price;
  const showInvalidCurrency =
    isPaymentProcess && !isNegotiation && price?.currency !== marketplaceCurrency;
  const PriceMissing = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.listingPriceMissing" />
      </p>
    );
  };

  const InvalidCurrency = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.listingCurrencyInvalid" />
      </p>
    );
  };

  const timeZone = listing?.attributes?.availabilityPlan?.timezone;
  const isClosed = listing?.attributes?.state === LISTING_STATE_CLOSED;
  const shouldHaveFixedBookingDuration = isBooking && [LINE_ITEM_FIXED].includes(lineItemUnitType);
  const showBookingFixedDurationForm =
    mounted && shouldHaveFixedBookingDuration && !isClosed && timeZone && priceVariants?.length > 0;
  const shouldHaveBookingTime = isBooking && [LINE_ITEM_HOUR].includes(lineItemUnitType);
  const showBookingTimeForm = mounted && shouldHaveBookingTime && !isClosed && timeZone;
  const shouldHaveBookingDates =
    isBooking && [LINE_ITEM_DAY, LINE_ITEM_NIGHT].includes(lineItemUnitType);
  const showBookingDatesForm = mounted && shouldHaveBookingDates && !isClosed && timeZone;

  // The listing resource has a relationship: `currentStock`,
  // which you should include when making API calls.
  const shouldHavePurchase = isPurchase && lineItemUnitType === LINE_ITEM_ITEM;
  const currentStock = listing.currentStock?.attributes?.quantity;
  const isOutOfStock = shouldHavePurchase && !isClosed && currentStock === 0;

  // Show form only when stock is fully loaded. This avoids "Out of stock" UI by
  // default before all data has been downloaded.
  const showProductOrderForm =
    mounted && shouldHavePurchase && !isClosed && typeof currentStock === 'number';

  const showInquiryForm = mounted && !isClosed && isInquiry;
  // if listing is a request, we show the negotiation form (reverse negotiation). User (provider) needs to make an offer first.
  const showNegotiationForm = mounted && !isClosed && isNegotiation && unitType === REQUEST;
  // if listing is an offer, we show the "request a quote" form as user needs to ask for a quote first from the provider.
  const showRequestQuoteForm = mounted && !isClosed && isNegotiation && unitType === OFFER;

  const supportedProcessesInfo = getSupportedProcessesInfo();
  const isKnownProcess = supportedProcessesInfo.map(info => info.name).includes(processName);

  const { pickupEnabled, shippingEnabled } = listing?.attributes?.publicData || {};
  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const displayShipping = displayDeliveryShipping(listingTypeConfig);
  const displayPickup = displayDeliveryPickup(listingTypeConfig);
  const allowOrdersOfMultipleItems = [STOCK_MULTIPLE_ITEMS, STOCK_INFINITE_MULTIPLE_ITEMS].includes(
    listingTypeConfig?.stockType
  );

  const authorDisplayName = userDisplayNameAsString(author, '');
  const searchParams = parse(location.search);
  const isOrderOpen = !!searchParams.orderOpen;
  const preselectedPriceVariantSlug = searchParams.bookableOption;

  const seatsEnabled = [AVAILABILITY_MULTIPLE_SEATS].includes(listingTypeConfig?.availabilityType);

  // Note: publicData contains priceVariationsEnabled if listing is created with priceVariations enabled.
  const isPriceVariationsInUse = !!publicData?.priceVariationsEnabled;
  const preselectedPriceVariant =
    Array.isArray(priceVariants) && preselectedPriceVariantSlug && isPriceVariationsInUse
      ? priceVariants.find(pv => pv?.name && createSlug(pv?.name) === preselectedPriceVariantSlug)
      : null;

  const priceVariantsMaybe = isPriceVariationsInUse
    ? {
        isPriceVariationsInUse,
        priceVariants,
        priceVariantFieldComponent: PriceVariantPicker,
        preselectedPriceVariant,
        isPublishedListing: isPublishedListing(listing),
      }
    : !isPriceVariationsInUse && showBookingFixedDurationForm
    ? {
        isPriceVariationsInUse: false,
        priceVariants: [getCheapestPriceVariant(priceVariants)],
        priceVariantFieldComponent: PriceVariantPicker,
      }
    : {};

  const showInvalidPriceVariantsMessage =
    isPriceVariationsInUse && !hasValidPriceVariants(priceVariants);

  const sharedProps = {
    lineItemUnitType,
    onSubmit,
    price,
    marketplaceCurrency,
    listingId: listing.id,
    isOwnListing,
    marketplaceName,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
  };

  const showClosedListingHelpText = listing.id && isClosed;

  const subTitleText = showClosedListingHelpText
    ? intl.formatMessage({ id: 'OrderPanel.subTitleClosedListing' })
    : null;

  const classes = classNames(rootClassName || css.root, className);
  const titleClasses = classNames(titleClassName || css.orderTitle);

  const awsPreviewFile = file => {
    return (
      <div className={css.fileBox}>
        <div className={css.fileInformation}>
          <PDFIconSVG />
          <div>
            <p
              className={css.fileName}
              onClick={() => window.open(file.location, '_blank')}
              title={file.key?.split('/')[1]?.replace(/^\d+-/, '')}
            >
              {file.key?.split('/')[1]?.replace(/^\d+-/, '')}
            </p>
            <p className={css.fileSize}>{(+file.size / 1000).toFixed()} KB</p>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (listing?.id?.uuid && author?.id?.uuid) {
      callFetchAuthor(author?.id?.uuid);
    }
  }, [listing?.id?.uuid]);

  const rawMsg = intl.formatMessage(
    { id: 'OrderPannel.whatsAppMsg' },
    { productTitle, brandName, marketplaceName }
  );
  const msg = encodeURIComponent(rawMsg);

  return (
    <div className={classes}>
      <ModalInMobile
        usePortal
        id="OrderFormInModal"
        isModalOpenOnMobile={isOrderOpen}
        showAsModalMaxWidth={MODAL_BREAKPOINT}
        containerClassName={css.modalContainer}
        onClose={() => closeOrderModal(history, location)}
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.priceAndRating}>
          {/* <PriceMaybe
            price={price}
            publicData={publicData}
            validListingTypes={validListingTypes}
            intl={intl}
            marketplaceCurrency={marketplaceCurrency}
          /> */}

          <section className={css.productName}>
            {richText(productTitle, {
              longWordClass: css.longWord,
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
            })}
          </section>

          {!isCurrentListingRfq && (
            <section className={css.productRating}>
              <div>
                <FormattedMessage id="OrderPannel.rating" />
              </div>
              <div>
                <FaStar />
                <span>{parseFloat(avgRating).toFixed(1)} / 5</span>
              </div>
            </section>
          )}
        </div>

        {brandPhoto || productBrandName ? (
          <div className={css.brandSection}>
            {brandPhoto && (
              <img className={css.brandImage} src={brandPhoto?.[0]?.location} alt="Preview" />
            )}
            <p className={css.brandName}>{productBrandName}</p>
          </div>
        ) : null}

        <p>{trimmedDescription}</p>

        {description?.length > DESC_MAX_WORDS && (
          <InlineTextButton onClick={e => setFullDesc(!fullDesc)} className={css.fullDesc}>
            {intl.formatMessage({
              id: fullDesc ? 'OrderPannel.hideDetail' : 'OrderPannel.viewDetails',
            })}{' '}
            {intl.formatMessage({ id: 'OrderPannel.completeDetails' })}
          </InlineTextButton>
        )}

        {/* categories for listing */}
        <section className={css.categoryDetails}>
          <div>
            <FormattedMessage id="OrderPannel.category" />
            <span>{categoryName?.[0]?.name}</span>
          </div>
          {subCategoryName?.length > 0 ? (
            <div>
              <FormattedMessage id="OrderPannel.subCategory" />
              <span>{subCategoryName?.[0]?.name}</span>
            </div>
          ) : null}
          {subSubCategoryName?.length > 0 ? (
            <div>
              <FormattedMessage id="OrderPannel.subSubCategory" />
              <span>{subSubCategoryName?.[0]?.name}</span>
            </div>
          ) : null}
        </section>

        {/* Services chips for listing */}
        {servicesType?.length ? (
          <div className={css.listingServiceChipContainer}>
            {/* {Array.from({ length: 12 }, () => servicesType).flat().map(service => ( */}
            {servicesType?.map(service => (
              <div key={service?.label}>{service?.label}</div>
            ))}
          </div>
        ) : null}

        {additionalUploadLink?.length > 0 ? (
          <div className={css.additionalDocumentsContainer}>
            <H4 className={css.documentsHeading}>
              <FormattedMessage id="OrderPannel.certificates" />
            </H4>
            {additionalUploadLink?.map((file, i) => (
              <div key={i} className={css.previewImageWrapper}>
                {awsPreviewFile(file)}
              </div>
            ))}
          </div>
        ) : null}

        {additionalDocuments?.length > 0 ? (
          <div className={css.additionalDocumentsContainer}>
            <H4 className={css.documentsHeading}>
              <FormattedMessage id="OrderPannel.document" />
            </H4>
            <div className={css.additionalDocuments}>
              {additionalDocuments?.map(file => (
                <div className={css.additionalDocument} title={file?.name}>
                  <p
                    className={css.fileName}
                    onClick={() => window.open(file.location, '_blank')}
                    title={file.key?.split('/')[1]?.replace(/^\d+-/, '')}
                  >
                    {file?.name?.replace(/^\d+-/, '')}
                  </p>
                  <p className={css.fileSize}>{(+file.size / 1000).toFixed()} KB</p>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* <div className={css.orderHeading}>
          {titleDesktop ? titleDesktop : <H2 className={titleClasses}>{title}</H2>}
          {subTitleText ? <div className={css.orderHelp}>{subTitleText}</div> : null}
        </div> */}

        {/* return (
    <div className={classes}>
      <ModalInMobile
        containerClassName={css.modalContainer}
        id="OrderFormInModal"
        isModalOpenOnMobile={isOrderOpen}
        onClose={() => closeOrderModal(history, location)}
        showAsModalMaxWidth={MODAL_BREAKPOINT}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
      >
        <div className={css.modalHeading}>
          <H1 className={css.heading}>{title}</H1>
        </div>

        {showListingImage && (
          <div className={css.orderHeading}>
            {titleDesktop ? titleDesktop : <H2 className={titleClasses}>{title}</H2>}
            {subTitleText ? <div className={css.orderHelp}>{subTitleText}</div> : null}
          </div>
        )}

        <PriceMaybe
          price={price}
          publicData={publicData}
          validListingTypes={validListingTypes}
          intl={intl}
          marketplaceCurrency={marketplaceCurrency}
        />

        <div className={css.author}>
          <AvatarSmall user={author} className={css.providerAvatar} />
          <span className={css.providerNameLinked}>
            <FormattedMessage id="OrderPanel.author" values={{ name: authorLink }} />
          </span>
          <span className={css.providerNamePlain}>
            <FormattedMessage id="OrderPanel.author" values={{ name: authorDisplayName }} />
          </span>
        </div> */}

        {showPriceMissing ? (
          <PriceMissing />
        ) : showInvalidCurrency ? (
          <InvalidCurrency />
        ) : showBookingTimeForm ? (
          <BookingTimeForm
            className={css.bookingForm}
            formId="OrderPanelBookingTimeForm"
            lineItemUnitType={lineItemUnitType}
            onSubmit={onSubmit}
            price={price}
            marketplaceCurrency={marketplaceCurrency}
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            listingId={listing.id}
            isOwnListing={isOwnListing}
            monthlyTimeSlots={monthlyTimeSlots}
          />
        ) : showInvalidPriceVariantsMessage ? (
          <InvalidPriceVariants />
        ) : showBookingFixedDurationForm ? (
          <BookingFixedDurationForm
            seatsEnabled={seatsEnabled}
            className={css.bookingForm}
            formId="OrderPanelBookingFixedDurationForm"
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            monthlyTimeSlots={monthlyTimeSlots}
            timeSlotsForDate={timeSlotsForDate}
            onFetchTimeSlots={onFetchTimeSlots}
            startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            startTimeInterval={startTimeInterval}
            timeZone={timeZone}
            finePrintComponent={SubmitFinePrint}
            {...priceVariantsMaybe}
            {...sharedProps}
          />
        ) : showBookingTimeForm ? (
          <BookingTimeForm
            seatsEnabled={seatsEnabled}
            className={css.bookingForm}
            formId="OrderPanelBookingTimeForm"
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            monthlyTimeSlots={monthlyTimeSlots}
            timeSlotsForDate={timeSlotsForDate}
            onFetchTimeSlots={onFetchTimeSlots}
            startDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            endDatePlaceholder={intl.formatDate(TODAY, dateFormattingOptions)}
            timeZone={timeZone}
            marketplaceName={marketplaceName}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            lineItems={lineItems}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            fetchLineItemsError={fetchLineItemsError}
            payoutDetailsWarning={payoutDetailsWarning}
            finePrintComponent={SubmitFinePrint}
            {...priceVariantsMaybe}
            {...sharedProps}
          />
        ) : showBookingDatesForm ? (
          <BookingDatesForm
            seatsEnabled={seatsEnabled}
            className={css.bookingForm}
            formId="OrderPanelBookingDatesForm"
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            monthlyTimeSlots={monthlyTimeSlots}
            onFetchTimeSlots={onFetchTimeSlots}
            timeZone={timeZone}
            marketplaceName={marketplaceName}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            lineItems={lineItems}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            fetchLineItemsError={fetchLineItemsError}
            payoutDetailsWarning={payoutDetailsWarning}
            finePrintComponent={SubmitFinePrint}
            {...priceVariantsMaybe}
            {...sharedProps}
          />
        ) : showProductOrderForm ? (
          <ProductOrderForm
            price={price}
            onSubmit={onSubmit}
            currentStock={currentStock}
            formId="OrderPanelProductOrderForm"
            marketplaceCurrency={marketplaceCurrency}
            allowOrdersOfMultipleItems={allowOrdersOfMultipleItems}
            pickupEnabled={pickupEnabled && displayPickup}
            shippingEnabled={shippingEnabled && displayShipping}
            displayDeliveryMethod={displayPickup || displayShipping}
            onContactUser={onContactUser}
            listingId={listing.id}
            isOwnListing={isOwnListing}
            marketplaceName={marketplaceName}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            lineItems={lineItems}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            fetchLineItemsError={fetchLineItemsError}
            payoutDetailsWarning={payoutDetailsWarning}
            {...sharedProps}
          />
        ) : showInquiryForm ? (
          <InquiryWithoutPaymentForm
            formId="OrderPanelInquiryForm"
            onSubmit={onSubmit}
            finePrintComponent={SubmitFinePrint}
            isOwnListing={isOwnListing}
          />
        ) : showNegotiationForm ? (
          <NegotiationForm
            formId="OrderPanelNegotiationForm"
            onSubmit={onSubmit}
            finePrintComponent={SubmitFinePrint}
            payoutDetailsWarning={payoutDetailsWarning}
            isOwnListing={isOwnListing}
          />
        ) : showRequestQuoteForm ? (
          <NegotiationRequestQuoteForm
            formId="OrderPanelRequestQuoteForm"
            onSubmit={onSubmit}
            finePrintComponent={SubmitFinePrint}
            payoutDetailsWarning={payoutDetailsWarning}
            isOwnListing={isOwnListing}
          />
        ) : !isKnownProcess ? (
          <p className={css.errorSidebar}>
            <FormattedMessage id="OrderPanel.unknownTransactionProcess" />
          </p>
        ) : null}

        <section className={css.authorSection}>
          {!isCurrentListingRfq ? (
            <div className={css.author}>
              <div className={css.authorAvatar}>
                <AvatarMedium user={author} className={css.providerAvatar} />
              </div>

              <div className={css.authorInformation}>
                {/* Business Name */}
                <div
                  className={css.authorCompany}
                  onClick={() => {
                    const params = { id: author?.id?.uuid };
                    history.push(
                      createResourceLocatorString('ProfilePage', routeConfiguration, params, {})
                    );
                  }}
                >
                  <span>{companyName}</span>
                  {isPremiumVendor && (
                    <React.Fragment>
                      <VerifiedSVG />
                      <span className={css.authorIsVerified}>
                        {intl.formatMessage({ id: 'UserProfile.verified' })}
                      </span>
                    </React.Fragment>
                  )}
                </div>

                {/* Provider Name */}
                {/* <div className={css.authorName}>{authorDisplayName}</div> */}

                {/* Chips */}
                {services && (
                  <div className={css.serviceChipContainer}>
                    {/* {Array.from({ length: 12 }, () => services).flat().map(service => ( */}
                    {services?.map(service => (
                      <div key={service?.label}>{service?.label}</div>
                    ))}
                  </div>
                )}

                {/* Email */}
                <div className={css.authorEmail}>
                  <EmailSVG />
                  {authorEmail ? (
                    <a className={css.anchorTag} href={`mailto:${authorEmail}`}>
                      {authorEmail}
                    </a>
                  ) : (
                    <FormattedMessage id="OrderPannel.loadingEmail" />
                  )}
                </div>

                {/* Phone */}
                {callDialCode && callNumber ? (
                  <div className={css.authorCall}>
                    <PhoneSVG />
                    <a className={css.anchorTag} href={`tel:${vendorCallNumber}`}>
                      {vendorCallNumber}
                    </a>
                  </div>
                ) : null}

                {/* WhatsApp */}
                {whatsAppDialCode && whatsAppNumber ? (
                  <div className={css.authorWhatsApp}>
                    <WhatsAppSVG />
                    <a
                      className={css.anchorTag}
                      target="_blank"
                      rel="noopener noreferrer"
                      href={`https://api.whatsapp.com/send?phone=${vendorWhatsAppNumber}&text=${msg}`}
                    >
                      {vendorWhatsAppNumber}
                    </a>
                  </div>
                ) : null}

                {/* Website Link */}
                {websiteLink ? (
                  <div className={css.authorWhatsApp}>
                    <GlobeSVG />
                    <a
                      className={css.anchorTag}
                      target="_blank"
                      rel="noopener noreferrer"
                      href={websiteLink}
                    >
                      {websiteLink}
                    </a>
                  </div>
                ) : null}

                {/* Social Media Link */}
                {socialMediaLink && isPremiumVendor ? (
                  <div className={css.authorWhatsApp}>
                    {/* <ShareSVG /> */}
                    <InstagramSVG className={css.contactIcon} />
                    <a
                      className={css.anchorTag}
                      target="_blank"
                      rel="noopener noreferrer"
                      href={socialMediaLink}
                    >
                      {socialMediaLink}
                    </a>
                  </div>
                ) : null}

                {linkedInLink && isPremiumVendor ? (
                  <div className={css.authorWhatsApp}>
                    <LinkedInSVG className={css.contactIcon} />
                    <a
                      className={css.anchorTag}
                      target="_blank"
                      rel="noopener noreferrer"
                      href={linkedInLink}
                    >
                      {linkedInLink}
                    </a>
                  </div>
                ) : null}

                {xHandle && isPremiumVendor ? (
                  <div className={css.authorWhatsApp}>
                    <XSVG className={css.contactIcon} />
                    <a
                      className={css.anchorTag}
                      target="_blank"
                      rel="noopener noreferrer"
                      href={xHandle}
                    >
                      {xHandle}
                    </a>
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <div className={css.author}>
              <div className={css.authorAvatar}>
                <AvatarMedium
                  user={author}
                  className={css.providerAvatar}
                  disableProfileLink={isCurrentListingRfq}
                />
              </div>

              <div className={css.authorInformation}>
                {/* Provider Name */}
                <div className={css.customerAsAuthorName}>
                  <span>{authorDisplayName}</span>
                </div>

                {/* Email */}
                <div className={css.authorEmail}>
                  <EmailSVG />
                  {authorEmail ? (
                    <a href={`mailto:${authorEmail}`}>{authorEmail}</a>
                  ) : (
                    <FormattedMessage id="OrderPannel.loadingEmail" />
                  )}
                </div>

                {/* Phone */}
                {showCustomerNumber && callDialCode && callNumber ? (
                  <div className={css.authorCall}>
                    <PhoneSVG />
                    <a href={`tel:${vendorCallNumber}`}>{vendorCallNumber}</a>
                  </div>
                ) : null}

                {/* WhatsApp */}
                {showCustomerNumber && whatsAppDialCode && whatsAppNumber ? (
                  <div className={css.authorWhatsApp}>
                    <WhatsAppSVG />
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={`https://api.whatsapp.com/send?phone=${vendorWhatsAppNumber}&text=${msg}`}
                    >
                      {vendorWhatsAppNumber}
                    </a>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </section>
      </ModalInMobile>

      <div className={css.openOrderForm}>
        {/* <PriceMaybe
          intl={intl}
          price={price}
          showCurrencyMismatch
          publicData={publicData}
          validListingTypes={validListingTypes}
          marketplaceCurrency={marketplaceCurrency}
        /> */}

        {isClosed ? (
          <div className={css.closedListingButton}>
            <FormattedMessage id="OrderPanel.closedListingButtonText" />
          </div>
        ) : (
          <PrimaryButton
            onClick={handleSubmit(
              isOwnListing,
              isClosed,
              showInquiryForm || showNegotiationForm,
              onSubmit,
              history,
              location
            )}
            disabled={isOutOfStock || isOwnListing}
          >
            {isBooking ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessageBooking" />
            ) : isOutOfStock ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessageNoStock" />
            ) : isPurchase ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessagePurchase" />
            ) : isOwnListing ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessageDisabled" />
            ) : showNegotiationForm ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessageMakeOffer" />
            ) : showRequestQuoteForm ? (
              <FormattedMessage id="OrderPanel.ctaButtonMessageRequestAQuote" />
            ) : (
              <FormattedMessage id="OrderPanel.ctaButtonMessageInquiry" />
            )}
          </PrimaryButton>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = state => {
  const { currentAuthor } = state.ListingPage;
  return { currentAuthor };
};

const mapDispatchToProps = dispatch => ({
  callFetchAuthor: authorId => dispatch(fetchAuthor(authorId)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(OrderPanel);
